// Phase 23.F — Simulator Guided Form.
//
// Replaces the flat amount/merchant/mcc/category grid in the Rule
// Simulator with a question flow that:
//
//   • Sources Portal/Channel and Subcategory values from the **Context
//     Registry** (not from this card's rules). Registry defines what is
//     possible; rules define what earns rewards. So an admin who has
//     seeded a new Subcategory value can simulate with it the same day,
//     without writing a rule first.
//
//   • Uses **this card's rules** to decide WHICH questions to ask and
//     in what order. If the card has zero rules referencing
//     `portal_channel`, the portal question is skipped. Same for
//     subcategory, mode and international. So a base-earn-only card
//     drops straight into "amount + category" with no extra noise.
//
//   • Maintains the SAME form-state shape the existing Simulator
//     expects, plus a new `contexts` field. The simulator API
//     (`/api/admin/simulate`) already accepts `contexts`, so the
//     backend doesn't need to change.
//
// Why a single component file: this only lives inside the Simulator
// panel and won't be reused elsewhere. Splitting per-step would create
// more cognitive load than it solves.

import { useEffect, useMemo, useState } from 'react';
import { Sparkles, Loader2, AlertCircle, Plug, Settings2, Globe2, MonitorSmartphone, Tag, Wallet } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useContextRegistry, getDefinitions } from '@/hooks/useContextRegistry';


// ─────────────────────────────────────────────────────────────────────
// Walk the card's rules to detect which dimensions are worth asking
// about. A dimension is "in use" if ANY non-excluded rule on the card
// has a non-empty value for it.
// ─────────────────────────────────────────────────────────────────────
function detectActiveDimensions(rules = []) {
  const dim = {
    portal_channel: false,
    subcategory: false,
    mode: false,            // any rule whose mode is not 'any'
    international: false,   // any rule whose international is true|false (not null)
    categories: new Set(),  // category values referenced by ANY rule (for ordering)
  };
  for (const r of rules) {
    if (!r || !r.match) continue;
    const m = r.match;
    for (const ctx of (m.contexts || [])) {
      if (ctx.context_type === 'portal_channel' && (ctx.values || []).length) {
        dim.portal_channel = true;
      }
      if (ctx.context_type === 'subcategory' && (ctx.values || []).length) {
        dim.subcategory = true;
      }
    }
    if (m.mode && m.mode !== 'any') dim.mode = true;
    if (m.international === true || m.international === false) dim.international = true;
    for (const c of (m.categories || [])) {
      if (c && typeof c === 'string') dim.categories.add(c.trim());
    }
  }
  return dim;
}


// ─────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────
export const SimulatorGuidedForm = ({
  cardRules = [],
  form,
  onChange,           // (patch) => void  — merges into parent form state
  contexts,           // { portal_channel: [...], subcategory: [...] }
  onContextsChange,   // (next) => void
}) => {
  const { registry, loading: registryLoading, error: registryError } = useContextRegistry();
  const portalDefs = useMemo(() => getDefinitions(registry, 'portal_channel'), [registry]);
  const subcatDefs = useMemo(() => getDefinitions(registry, 'subcategory'), [registry]);

  // Which dimensions does this card actually use? Drives question visibility.
  const dim = useMemo(() => detectActiveDimensions(cardRules), [cardRules]);

  // Set for chip decoration: highlight portals/subcategories that this card
  // has an accelerator for. Purely cosmetic — every registry value remains
  // selectable.
  const ruleReferencedPortals = useMemo(() => {
    const s = new Set();
    for (const r of cardRules) {
      for (const c of (r?.match?.contexts || [])) {
        if (c.context_type === 'portal_channel') (c.values || []).forEach((v) => s.add(String(v).toLowerCase()));
      }
    }
    return s;
  }, [cardRules]);
  const ruleReferencedSubcats = useMemo(() => {
    const s = new Set();
    for (const r of cardRules) {
      for (const c of (r?.match?.contexts || [])) {
        if (c.context_type === 'subcategory') (c.values || []).forEach((v) => s.add(String(v).toLowerCase()));
      }
    }
    return s;
  }, [cardRules]);

  // Suggested category chips — first 6 distinct categories referenced by the
  // card's rules. Helps admins click instead of type.
  const suggestedCategories = useMemo(() => {
    return Array.from(dim.categories).slice(0, 6);
  }, [dim.categories]);

  const currentPortals = contexts?.portal_channel || [];
  const currentSubcats = contexts?.subcategory || [];

  // When the user picks a portal, narrow the subcategory list by category
  // hint (if the chosen portal value has related_categories, we can pre-
  // emphasise matching subcategories — same logic the Rule Builder uses).
  const subcatHint = useMemo(() => {
    if (!form.category || !subcatDefs.length) return null;
    const cat = String(form.category).toLowerCase();
    const matchingSubcatValues = new Set();
    for (const d of subcatDefs) {
      const rc = (d.related_categories || []).map((s) => String(s).toLowerCase());
      if (rc.includes(cat)) matchingSubcatValues.add(d.value);
    }
    return matchingSubcatValues;
  }, [form.category, subcatDefs]);

  // Visibility flags — drive which question cards render.
  const askPortal       = dim.portal_channel;
  const askSubcategory  = dim.subcategory;
  const askMode         = dim.mode;
  const askIntl         = dim.international;

  // No questions at all? Card is a base-earn-only setup — fall back to a
  // minimal amount-and-category form.
  const minimal = !askPortal && !askSubcategory && !askMode && !askIntl;

  return (
    <div className="space-y-3" data-testid="admin-simulator-guided-form">
      {registryError && (
        <Card className="p-2 border-amber-500/40 bg-amber-500/5 text-[11px] text-amber-700 dark:text-amber-400 flex gap-2">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>Context registry failed to load — Portal / Subcategory questions disabled. Refresh to retry.</span>
        </Card>
      )}

      {/* Question 1 — always: amount + category */}
      <QuestionCard
        icon={Tag}
        index={1}
        title="What's the spend?"
        hint="Amount and category drive everything else"
      >
        <div className="grid grid-cols-2 gap-2">
          <Field label="Amount (₹)">
            <Input
              type="number"
              inputMode="numeric"
              value={form.amount}
              onChange={(e) => onChange({ amount: e.target.value })}
              className="h-9 font-mono-num text-sm"
              data-testid="admin-simulator-guided-amount"
            />
          </Field>
          <Field label="Category">
            <Input
              value={form.category}
              onChange={(e) => onChange({ category: e.target.value })}
              className="h-9 text-sm"
              placeholder="e.g. Travel"
              data-testid="admin-simulator-guided-category"
            />
          </Field>
        </div>
        {suggestedCategories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mr-1 self-center">Used by rules:</span>
            {suggestedCategories.map((c) => (
              <SuggestionChip
                key={c}
                active={String(form.category || '').toLowerCase() === c.toLowerCase()}
                onClick={() => onChange({ category: c })}
                testid={`admin-simulator-guided-cat-${c.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {c}
              </SuggestionChip>
            ))}
          </div>
        )}
      </QuestionCard>

      {minimal && (
        <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))]/30 p-3 text-[11px] text-muted-foreground" data-testid="admin-simulator-guided-minimal-hint">
          <span className="font-medium text-foreground">This card has no portal/subcategory/mode accelerators.</span> Run with just amount + category — the simulator will fire base-earn rules. (Advanced inputs for period usage and milestone progress still available below.)
        </div>
      )}

      {/* Question 2 — Portal (only if card has portal rules) */}
      {askPortal && (
        <QuestionCard
          icon={Plug}
          index={2}
          title="Through a bank portal?"
          hint="Select one. Pick None if this is a direct merchant transaction."
        >
          {registryLoading && !portalDefs.length ? (
            <SkeletonRow />
          ) : (
            <ChipPickerSingle
              testidPrefix="admin-simulator-guided-portal"
              none={{ label: 'None / Direct', testidSuffix: 'none' }}
              options={portalDefs}
              ruleReferenced={ruleReferencedPortals}
              value={currentPortals[0] || ''}
              onChange={(v) => onContextsChange({
                ...contexts,
                portal_channel: v ? [v] : [],
              })}
            />
          )}
        </QuestionCard>
      )}

      {/* Question 3 — Subcategory (only if card has subcategory rules) */}
      {askSubcategory && (
        <QuestionCard
          icon={Settings2}
          index={(askPortal ? 3 : 2)}
          title={currentPortals.length > 0
            ? `What are you purchasing through ${portalLabel(portalDefs, currentPortals[0])}?`
            : 'What kind of purchase?'}
          hint="Optional. Pick Other / None if not applicable."
        >
          {registryLoading && !subcatDefs.length ? (
            <SkeletonRow />
          ) : (
            <ChipPickerSingle
              testidPrefix="admin-simulator-guided-subcat"
              none={{ label: 'Other / None', testidSuffix: 'none' }}
              options={subcatDefs}
              ruleReferenced={ruleReferencedSubcats}
              hint={subcatHint}
              value={currentSubcats[0] || ''}
              onChange={(v) => onContextsChange({
                ...contexts,
                subcategory: v ? [v] : [],
              })}
            />
          )}
        </QuestionCard>
      )}

      {/* Question — Mode (only if card has online/offline rules) */}
      {askMode && (
        <QuestionCard
          icon={MonitorSmartphone}
          index={2 + (askPortal ? 1 : 0) + (askSubcategory ? 1 : 0)}
          title="Online or offline?"
        >
          <ChipPickerSingle
            testidPrefix="admin-simulator-guided-mode"
            options={[
              { value: 'online',  label: 'Online' },
              { value: 'offline', label: 'Offline' },
            ]}
            value={form.mode}
            onChange={(v) => onChange({ mode: v })}
          />
        </QuestionCard>
      )}

      {/* Question — International (only if card has intl rules) */}
      {askIntl && (
        <QuestionCard
          icon={Globe2}
          index={2 + (askPortal ? 1 : 0) + (askSubcategory ? 1 : 0) + (askMode ? 1 : 0)}
          title="International transaction?"
        >
          <ChipPickerSingle
            testidPrefix="admin-simulator-guided-intl"
            options={[
              { value: 'no',  label: 'Domestic (₹ INR)' },
              { value: 'yes', label: 'International' },
            ]}
            value={form.international ? 'yes' : 'no'}
            onChange={(v) => onChange({ international: v === 'yes' })}
          />
        </QuestionCard>
      )}

      {/* Optional merchant + MCC — collapsible "specifics" */}
      <details className="rounded-md border border-border bg-[hsl(var(--surface-2))]/30 p-2.5" data-testid="admin-simulator-guided-specifics">
        <summary className="flex items-center gap-1.5 cursor-pointer text-[11px] font-medium text-muted-foreground hover:text-foreground list-none">
          <Wallet className="h-3 w-3" />
          Specific merchant or MCC (optional)
          <Badge variant="outline" className="text-[9px] ml-auto">most rules don't need this</Badge>
        </summary>
        <div className="mt-2 grid grid-cols-2 gap-2">
          <Field label="Merchant">
            <Input
              value={form.merchant}
              onChange={(e) => onChange({ merchant: e.target.value })}
              className="h-8 text-xs"
              placeholder="swiggy, makemytrip…"
              data-testid="admin-simulator-guided-merchant"
            />
          </Field>
          <Field label="MCC">
            <Input
              value={form.mcc}
              onChange={(e) => onChange({ mcc: e.target.value })}
              className="h-8 font-mono-num text-xs"
              placeholder="5814"
              data-testid="admin-simulator-guided-mcc"
            />
          </Field>
        </div>
      </details>
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────
function portalLabel(defs, value) {
  const hit = defs.find((d) => d.value === value);
  return hit?.label || value;
}

const QuestionCard = ({ icon: Icon, index, title, hint, children }) => (
  <div className="rounded-md border border-border bg-background/60 p-2.5" data-testid={`admin-simulator-guided-q${index}`}>
    <div className="flex items-baseline gap-1.5 mb-2">
      <span className="text-[10px] font-mono-num text-muted-foreground shrink-0">Q{index}.</span>
      {Icon ? <Icon className="h-3.5 w-3.5 text-accent shrink-0" /> : null}
      <div className="text-[12px] font-medium">{title}</div>
    </div>
    {hint && <div className="text-[10px] text-muted-foreground -mt-1.5 mb-1.5 ml-5">{hint}</div>}
    <div className="ml-0">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <Label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </label>
);

const SuggestionChip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={cn(
      'text-[10px] rounded-full px-2 py-0.5 border transition-colors',
      active
        ? 'bg-primary/15 border-primary/40 text-primary font-medium'
        : 'border-border bg-[hsl(var(--surface-2))] hover:bg-accent/10 hover:border-accent/30 text-muted-foreground',
    )}
  >
    {children}
  </button>
);

const SkeletonRow = () => (
  <div className="flex flex-wrap gap-1.5 animate-pulse">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="h-7 w-20 rounded-md bg-muted/30" />
    ))}
  </div>
);

// Single-select chip picker. Renders registry options + an optional
// "None" pill. Rule-referenced options get an accent border so admins
// see at a glance which values their card actually accelerates on.
const ChipPickerSingle = ({ testidPrefix, options, ruleReferenced, hint, value, onChange, none }) => {
  const isNone = !value;
  // Sort: rule-referenced first, then category-hinted, then alphabetical.
  const sorted = useMemo(() => {
    const arr = [...options];
    arr.sort((a, b) => {
      const aRef = ruleReferenced?.has(String(a.value).toLowerCase()) ? 1 : 0;
      const bRef = ruleReferenced?.has(String(b.value).toLowerCase()) ? 1 : 0;
      if (aRef !== bRef) return bRef - aRef;
      const aHint = hint?.has(a.value) ? 1 : 0;
      const bHint = hint?.has(b.value) ? 1 : 0;
      if (aHint !== bHint) return bHint - aHint;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
    return arr;
  }, [options, ruleReferenced, hint]);

  return (
    <div className="flex flex-wrap gap-1.5">
      {none && (
        <button
          type="button"
          onClick={() => onChange('')}
          data-testid={`${testidPrefix}-${none.testidSuffix}`}
          className={cn(
            'text-[11px] rounded-md border px-2 py-1 transition-colors',
            isNone
              ? 'bg-muted text-foreground border-border font-medium'
              : 'bg-background border-input text-muted-foreground hover:bg-muted/30',
          )}
        >
          {none.label}
        </button>
      )}
      {sorted.map((d) => {
        const active = String(value || '').toLowerCase() === String(d.value).toLowerCase();
        const refed = ruleReferenced?.has(String(d.value).toLowerCase());
        return (
          <button
            key={d.value}
            type="button"
            onClick={() => onChange(d.value)}
            data-testid={`${testidPrefix}-${String(d.value).toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
            className={cn(
              'text-[11px] rounded-md border px-2 py-1 transition-colors inline-flex items-center gap-1',
              active
                ? 'bg-primary/15 border-primary/40 text-primary font-medium'
                : refed
                  ? 'bg-background border-primary/30 text-foreground hover:bg-primary/5'
                  : 'bg-background border-input text-muted-foreground hover:bg-muted/30',
            )}
            title={refed ? 'This card has an accelerator for this value' : 'Valid value — base rate applies on this card'}
          >
            {d.label || d.value}
            {refed && <span className="h-1 w-1 rounded-full bg-primary" aria-label="accelerated" />}
          </button>
        );
      })}
    </div>
  );
};
